<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWoowCrm } from 'dashboard/composables/useWoowCrm';
import WoowCrmAPI from 'dashboard/api/integrations/woowCrm';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactName: { type: String, default: '' },
});

const { t } = useI18n();
const conversationId = computed(() => props.conversationId);
const { linkedCustomer } = useWoowCrm(conversationId);

const dialogRef = ref(null);
const isSaving = ref(false);
const categories = ref([]);
const priorities = ref([]);
const assignees = ref([]);
const relatedMessageId = ref(null);

const form = ref({
  title: '',
  description: '',
  category: 'inquiry',
  priority: 'medium',
  assigned_agent_id: null,
  deadline: '',
});

const assigneeOptions = computed(() =>
  assignees.value.map(agent => ({ value: agent.id, label: agent.name }))
);

const isValid = computed(
  () =>
    form.value.title.trim() &&
    form.value.description.trim() &&
    form.value.assigned_agent_id &&
    form.value.deadline
);

const loadOptions = async () => {
  try {
    const { data } = await WoowCrmAPI.getFormOptions();
    categories.value = data.categories ?? [];
    priorities.value = data.priorities ?? [];
    assignees.value = data.assignees ?? [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WOOW_CRM.TICKET.OPTIONS_ERROR')
    );
  }
};

// Two days out — long enough that a ticket raised mid-conversation is not
// already overdue, short enough to stay in the current triage window.
const defaultDeadline = () => {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().split('T')[0];
};

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const { data } = await WoowCrmAPI.createTicket(props.conversationId, {
      ...form.value,
      related_message_id: relatedMessageId.value,
    });
    useAlert(t('WOOW_CRM.TICKET.CREATED', { id: data.id }));
    dialogRef.value.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WOOW_CRM.TICKET.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const truncate = (text, length) =>
  text.length > length ? `${text.slice(0, length - 1)}…` : text;

const open = (message = null) => {
  const name = props.contactName || t('WOOW_CRM.CUSTOMER');
  const snippet = (message?.content ?? '').trim();
  relatedMessageId.value = message?.id ?? null;

  form.value = {
    title: snippet
      ? t('WOOW_CRM.TICKET.TITLE_FROM_MESSAGE', {
          snippet: truncate(snippet, 60),
        })
      : t('WOOW_CRM.TICKET.TITLE_FROM_CONVERSATION', { name }),
    description: snippet
      ? `“${truncate(snippet, 500)}”\n\n${t('WOOW_CRM.TICKET.CONTEXT', { id: props.conversationId, name })}`
      : t('WOOW_CRM.TICKET.CONTEXT', { id: props.conversationId, name }),
    category: 'inquiry',
    priority: 'medium',
    assigned_agent_id: null,
    deadline: defaultDeadline(),
  };

  loadOptions();
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WOOW_CRM.TICKET.TITLE')"
    :confirm-button-label="t('WOOW_CRM.TICKET.SUBMIT')"
    :disable-confirm-button="!isValid"
    :is-loading="isSaving"
    width="xl"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <p v-if="linkedCustomer" class="text-xs text-n-slate-11">
        {{ t('WOOW_CRM.TICKET.FOR_CUSTOMER', { ste: linkedCustomer.user_id }) }}
      </p>
      <p v-else class="text-xs text-n-amber-11">
        {{ t('WOOW_CRM.TICKET.NO_CUSTOMER') }}
      </p>

      <Input
        v-model="form.title"
        :label="t('WOOW_CRM.TICKET.FIELDS.TITLE')"
        autofocus
      />

      <TextArea
        v-model="form.description"
        :label="t('WOOW_CRM.TICKET.FIELDS.DESCRIPTION')"
        :max-length="2000"
        auto-height
      />

      <div class="grid grid-cols-2 gap-4">
        <Select
          v-model="form.category"
          :options="categories"
          :label="t('WOOW_CRM.TICKET.FIELDS.CATEGORY')"
        />
        <Select
          v-model="form.priority"
          :options="priorities"
          :label="t('WOOW_CRM.TICKET.FIELDS.PRIORITY')"
        />
        <Select
          v-model="form.assigned_agent_id"
          :options="assigneeOptions"
          :label="t('WOOW_CRM.TICKET.FIELDS.ASSIGNEE')"
          :placeholder="t('WOOW_CRM.TICKET.FIELDS.ASSIGNEE_PLACEHOLDER')"
        />
        <Input
          v-model="form.deadline"
          type="date"
          :label="t('WOOW_CRM.TICKET.FIELDS.DEADLINE')"
        />
      </div>
    </div>
  </Dialog>
</template>
